import * as React from "react"
import { MultiTagInput } from "@nexus/ui"
import type { MultiTagInputProps } from "@nexus/ui"
import { useTranslation } from "react-i18next"

export type MultiTagInputWithSelectAllProps = MultiTagInputProps & {
  selectedOfTotalLabel?: (selected: number, total: number) => string
  noMatchText?: (query: string) => string
}

export function MultiTagInputWithSelectAll({
  options = [],
  value = [],
  onChange,
  loading,
  disabled,
  disableFreeText = false,
  selectedOfTotalLabel,
  noMatchText,
  ...rest
}: MultiTagInputWithSelectAllProps) {
  const { t } = useTranslation()

  const allValues = React.useMemo(
    () => options.map((opt) => opt.value ?? opt.label),
    [options],
  )

  const selectedCount = React.useMemo(
    () => value.filter((v) => allValues.includes(v)).length,
    [value, allValues],
  )

  const canBulkAction = !disabled && !loading && allValues.length > 0
  const allSelected = allValues.length > 0 && selectedCount === allValues.length

  const resolveSelectedOfTotalLabel =
    selectedOfTotalLabel ??
    ((selected: number, total: number) =>
      t("opportunities.settingsIndustryCompetitorsSelectedOfTotal", {
        selected,
        total,
      }))

  const resolveNoMatchText =
    noMatchText ??
    ((query: string) =>
      t("opportunities.settingsIndustryCompetitorsNoMatch", { query }))

  return (
    <MultiTagInput
      options={options}
      value={value}
      onChange={onChange}
      loading={loading}
      disabled={disabled}
      disableFreeText={disableFreeText}
      bulkActions={
        canBulkAction
          ? {
              selectedOfTotalLabel: resolveSelectedOfTotalLabel(selectedCount, allValues.length),
              selectAllLabel: t("common.selectAll"),
              clearLabel: t("common.clear"),
              onSelectAll: () => {
                const freeText = disableFreeText
                  ? []
                  : value.filter((v) => !allValues.includes(v))
                onChange?.([...freeText, ...allValues])
              },
              onClear: () => onChange?.([]),
              showSelectAll: !allSelected,
            }
          : undefined
      }
      noMatchText={resolveNoMatchText}
      {...rest}
    />
  )
}
